///
/// EarthBrowser
///
/// Created by Matt Giger on 10/10/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Analytics event taxonomy shared by the client (`utils/analytics.ts`)
/// and the first-party ingest endpoint (`api-server/src/metrics`). The
/// server rejects any event name or property key not listed here, so the
/// metrics table can never accumulate unplanned data. Adding an event
/// means adding its props interface AND its allowlist row below; the
/// `satisfies` clause keeps the two in sync at compile time.
///
/// Design: documentation/planning/ANALYTICS_STRATEGY.md §5
///

/** How the visitor arrived (derived from URL params + referrer on boot) */
export type AnalyticsEntry = 'organic' | 'deeplink' | 'share' | 'story' | 'embed' | 'referral'

export type AnalyticsPlatform = 'web' | 'tauri' | 'embed' | 'ios' | 'android'

export type ArtifactKind = 'view' | 'placemark' | 'story' | 'share' | 'embed' | 'dataset'

/** Per-event property shapes. Keep values scalar or short string arrays. */
export interface AnalyticsEventProps {
	// ---- lifecycle ----
	app_launched: {
		entry: AnalyticsEntry
		referrer_host?: string
		utm_source?: string
		utm_medium?: string
		utm_campaign?: string
		route: string
		viewport: string
		/** Previous session count was 0 (first launch on this install) */
		cold: boolean
	}
	app_ready: {
		first_frame_ms: number
		/** navigator.hardwareConcurrency (capped at 32) */
		hw_threads?: number
		/** navigator.deviceMemory where exposed (Chromium only) */
		device_memory_gb?: number
	}
	first_interaction: {
		since_load_ms: number
		kind: 'pointer' | 'wheel' | 'touch' | 'key' | 'ui'
	}
	session_heartbeat: {
		active_s: number
		layers: string[]
		fps_p50?: number
		fps_p10?: number
		long_frames?: number
		gpu_mb?: number
		gpu_peak_mb?: number
		under_pressure?: boolean
	}
	session_ended: {
		active_s: number
		interactions: number
		layers_max: number
		windows_opened: number
	}
	session_crashed: {
		crumbs: string[]
		/** Consecutive crashes inside the sentinel's loop window (1..3) */
		level?: number
		/** Heavy layer blamed: the one being restored at the kill, else the last enabled */
		suspect?: string
		/** Heavy layers on when the tab died */
		layers?: string[]
		/** The browser reloaded the same tab (vs a later launch finding a stale session) */
		same_tab?: boolean
		/** Set when the page reloaded itself on a visible failure (webgl-context-lost) */
		reason?: string
	}
	/** CrashRecovery outcome on the launch after a crash */
	crash_recovery: {
		level: number
		outcome: 'restored' | 'restore_requested' | 'suspect_skipped'
		layers: string[]
		suspect?: string
	}
	/**
	 * Browser-reported crash (Reporting API, Chromium/Firefox; server-side
	 * only, from POST /api/crash-reports). `layers` comes from the page's
	 * crashReport context when the browser supports it
	 */
	browser_crash: {
		reason?: 'oom' | 'unresponsive' | 'unknown'
		visibility?: string
		layers?: string[]
		path?: string
	}

	// ---- layers & globe ----
	layer_enabled: {
		layer: string
		since_load_s: number
		/** launch = restored/deep-linked before any input; user = after input */
		source: 'launch' | 'user'
	}
	layer_disabled: {
		layer: string
		dwell_s: number
	}
	layer_blocked_memory: {
		layer: string
		predicted_mb: number
	}
	/** The gate let a heavy layer on by switching the session to lite mode */
	layer_enabled_lite: {
		layer: string
		predicted_mb: number
	}
	/** The session went lite on its own (page memory watch), not via the gate or recovery */
	lite_mode_entered: {
		source: 'memory_watch'
		mb: number
	}
	layer_blocked_texture_units: {
		layer: string
		units: number
	}
	entity_selected: {
		layer: string
	}
	time_control_used: {
		mode: 'scrub' | 'play' | 'pause' | 'realtime' | 'speed' | 'range' | 'expand'
	}
	camera_mode_changed: {
		mode: 'free' | 'orbit' | 'tour' | 'tourPaused' | 'follow' | 'heading'
	}
	search_used: {
		result_kind?: string
		had_result: boolean
	}
	view_recalled: {
		source?: 'list' | 'palette' | 'deeplink' | 'prompt'
	}
	window_opened: {
		window_type: string
	}
	file_imported: {
		format: string
		size_bucket: 'xs' | 's' | 'm' | 'l' | 'xl'
		ok: boolean
	}

	// ---- authoring & spread ----
	artifact_created: {
		kind: ArtifactKind
	}
	story_played: {
		stops: number
		guided: boolean
	}
	story_stop_viewed: {
		index: number
		dwell_s: number
	}
	story_completed: {
		stops: number
		total_s: number
	}
	share_link_opened: {
		share_type: string
	}
	embed_loaded: {
		target_kind?: string
	}
	embed_interacted: Record<string, never>

	// ---- funnel (PostHog-first; also mirrored to Tier 1 as counts) ----
	eb_session_started: {
		session_count: number
		entered_via_deep_link: boolean
		first_session_was_deep_link: boolean
		days_since_first_seen: number
	}
	onboarding_started: Record<string, never>
	onboarding_completed: { steps_viewed: number }
	onboarding_skipped: { skipped_at_step: number; steps_viewed: number }
	register_chip_clicked: { rung: number }
	auth_dialog_opened: { rung: number }
	account_created: Record<string, never>
	account_deleted: Record<string, never>
	conversion_banner_shown: { session_count: number; rung: number }
	conversion_banner_clicked: { rung: number }
	conversion_banner_dismissed: { rung: number }
	support_interstitial_shown: { session_count: number; show_count: number; dismiss_delay_s: number; rung: number }
	support_interstitial_cta_clicked: { signed_in: boolean; rung: number }
	signin_wall_shown: { session_count: number }
	signin_wall_cta_clicked: Record<string, never>
	saved_view_prompt_shown: { view_count: number }
	saved_view_prompt_clicked: Record<string, never>
	saved_view_prompt_dismissed: Record<string, never>
	full_access_offer_shown: Record<string, never>
	full_access_offer_accepted: Record<string, never>
	full_access_offer_snoozed: Record<string, never>
	supporter_offer_viewed: { signed_in: boolean }
	supporter_offer_checkout_started: Record<string, never>
	share_make_your_own_clicked: { signed_in: boolean }
	/** Share button in the top-right cluster: link to the current view */
	view_shared: { method: 'native' | 'clipboard' }
	experiment_exposed: { flag: string; variant: string }
}

export type AnalyticsEventName = keyof AnalyticsEventProps

/**
 * Server-side allowlist: event name → permitted property keys. Derived by
 * hand from the interfaces above; the `satisfies` keeps names and keys
 * from drifting (a typo here is a compile error).
 */
export const cAnalyticsAllowlist = {
	app_launched: ['entry', 'referrer_host', 'utm_source', 'utm_medium', 'utm_campaign', 'route', 'viewport', 'cold'],
	app_ready: ['first_frame_ms', 'hw_threads', 'device_memory_gb'],
	first_interaction: ['since_load_ms', 'kind'],
	session_heartbeat: ['active_s', 'layers', 'fps_p50', 'fps_p10', 'long_frames', 'gpu_mb', 'gpu_peak_mb', 'under_pressure'],
	session_ended: ['active_s', 'interactions', 'layers_max', 'windows_opened'],
	session_crashed: ['crumbs', 'level', 'suspect', 'layers', 'same_tab', 'reason'],
	crash_recovery: ['level', 'outcome', 'layers', 'suspect'],
	browser_crash: ['reason', 'visibility', 'layers', 'path'],
	layer_enabled: ['layer', 'since_load_s', 'source'],
	layer_disabled: ['layer', 'dwell_s'],
	layer_blocked_memory: ['layer', 'predicted_mb'],
	layer_enabled_lite: ['layer', 'predicted_mb'],
	lite_mode_entered: ['source', 'mb'],
	layer_blocked_texture_units: ['layer', 'units'],
	entity_selected: ['layer'],
	time_control_used: ['mode'],
	camera_mode_changed: ['mode'],
	search_used: ['result_kind', 'had_result'],
	view_recalled: ['source'],
	window_opened: ['window_type'],
	file_imported: ['format', 'size_bucket', 'ok'],
	artifact_created: ['kind'],
	story_played: ['stops', 'guided'],
	story_stop_viewed: ['index', 'dwell_s'],
	story_completed: ['stops', 'total_s'],
	share_link_opened: ['share_type'],
	embed_loaded: ['target_kind'],
	embed_interacted: [],
	eb_session_started: ['session_count', 'entered_via_deep_link', 'first_session_was_deep_link', 'days_since_first_seen'],
	onboarding_started: [],
	onboarding_completed: ['steps_viewed'],
	onboarding_skipped: ['skipped_at_step', 'steps_viewed'],
	register_chip_clicked: ['rung'],
	auth_dialog_opened: ['rung'],
	account_created: [],
	account_deleted: [],
	conversion_banner_shown: ['session_count', 'rung'],
	conversion_banner_clicked: ['rung'],
	conversion_banner_dismissed: ['rung'],
	support_interstitial_shown: ['session_count', 'show_count', 'dismiss_delay_s', 'rung'],
	support_interstitial_cta_clicked: ['signed_in', 'rung'],
	signin_wall_shown: ['session_count'],
	signin_wall_cta_clicked: [],
	saved_view_prompt_shown: ['view_count'],
	saved_view_prompt_clicked: [],
	saved_view_prompt_dismissed: [],
	full_access_offer_shown: [],
	full_access_offer_accepted: [],
	full_access_offer_snoozed: [],
	supporter_offer_viewed: ['signed_in'],
	supporter_offer_checkout_started: [],
	share_make_your_own_clicked: ['signed_in'],
	view_shared: ['method'],
	experiment_exposed: ['flag', 'variant'],
} as const satisfies { [K in AnalyticsEventName]: readonly (keyof AnalyticsEventProps[K] & string)[] }

/**
 * Events that are also forwarded to PostHog (Tier 2) when the visitor has
 * performance consent. Everything else is Tier 1 only: high-frequency
 * engagement/perf signal that would burn PostHog volume without adding
 * funnel insight.
 */
export const cPostHogEvents: ReadonlySet<AnalyticsEventName> = new Set<AnalyticsEventName>([
	'app_ready',
	'layer_enabled',
	'artifact_created',
	'story_played',
	'story_completed',
	'share_link_opened',
	'eb_session_started',
	'onboarding_started',
	'onboarding_completed',
	'onboarding_skipped',
	'register_chip_clicked',
	'auth_dialog_opened',
	'account_created',
	'account_deleted',
	'conversion_banner_shown',
	'conversion_banner_clicked',
	'conversion_banner_dismissed',
	'support_interstitial_shown',
	'support_interstitial_cta_clicked',
	'signin_wall_shown',
	'signin_wall_cta_clicked',
	'saved_view_prompt_shown',
	'saved_view_prompt_clicked',
	'saved_view_prompt_dismissed',
	'full_access_offer_shown',
	'full_access_offer_accepted',
	'full_access_offer_snoozed',
	'supporter_offer_viewed',
	'supporter_offer_checkout_started',
	'share_make_your_own_clicked',
	'view_shared',
	'experiment_exposed',
])

/** One event as sent over the wire (server adds country/device/browser) */
export interface AnalyticsWireEvent {
	/** Client wall-clock ms; server clamps to [received - 1h, received + 5min] */
	ts: number
	event: AnalyticsEventName
	props: Record<string, unknown>
}

/** Request body for POST /api/metrics */
export interface AnalyticsBatch {
	/** Random per-page-load UUID; never persisted */
	session_id: string
	platform: AnalyticsPlatform
	app_version: string
	/**
	 * Optional consented subject identifier (§4.3 fallback). Only sent when
	 * the client's subject flag is on AND performance consent is granted;
	 * the server stores only an HMAC of it.
	 */
	subject?: string
	events: AnalyticsWireEvent[]
}

/** Ingest limits shared so client batching matches server validation */
export const cAnalyticsLimits = {
	maxEventsPerBatch: 50,
	maxBodyBytes: 16 * 1024,
	maxStringLength: 120,
	maxArrayLength: 16,
} as const
